import React from 'react';
import PartnerPageTemplate from './PartnerPageTemplate';
import {
  Hospital,
  Building2,
  Users,
  HeartPulse,
  Repeat,
  ShieldCheck,
  FileCheck,
  MapPin,
  Phone,
  ClipboardList,
} from 'lucide-react';

const CANONICAL = 'https://www.moveracaremobility.com/healthcare-partners/healthcare-facilities';

const HealthcareFacilities = () => {
  const seo = {
    title: 'Healthcare Facility Transportation | Hospitals, SNFs, Clinics | MoveraCare',
    description: 'Non-emergency medical transportation for hospitals, skilled nursing facilities, clinics, and outpatient centers across Greater Houston. Wheelchair and ambulatory NEMT with credentialing-ready documentation.',
    canonical: CANONICAL,
    schemaId: 'partner-healthcare-facilities-schema',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Healthcare Facility Transportation',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        telephone: '+1-832-925-2128',
        url: 'https://www.moveracaremobility.com',
      },
      audience: {
        '@type': 'MedicalAudience',
        audienceType: 'Healthcare Facilities, Hospitals, SNFs, Clinics',
      },
    },
  };

  return (
    <PartnerPageTemplate
      testid="partner-healthcare-facilities"
      seo={seo}
      eyebrow="HEALTHCARE FACILITIES"
      heading="Transportation Support for Hospitals, SNFs, Clinics & Outpatient Facilities"
      intro="MoveraCare Mobility provides reliable wheelchair and ambulatory transportation for healthcare facilities coordinating patient movement across Greater Houston — from discharges and outpatient appointments to recurring treatment and follow-up care."
      audience={{
        title: 'Built for Facility Discharge, Transfer & Appointment Coordination',
        description: 'This page is designed for healthcare facility teams responsible for coordinating patient transportation — including discharge planners, unit case managers, admissions, social workers, transport coordinators, and facility administrators.',
        bullets: [
          'Hospitals coordinating discharges to home, SNF, or rehab',
          'Skilled nursing facilities managing resident appointments',
          'Outpatient clinics scheduling patient arrivals and returns',
          'Rehabilitation centers coordinating therapy transport',
          'Long-term care and assisted living facility teams',
          'Facility administrators evaluating transportation vendors',
        ],
      }}
      problem={{
        title: 'Facility Transportation Is Often the Bottleneck',
        description: 'Discharge delays, missed appointments, and last-minute transport gaps disrupt care coordination and impact throughput. MoveraCare provides transportation capacity your facility can rely on — coordinated with facility workflows, not against them.',
        bullets: [
          'Same-day and scheduled discharge transportation to home, SNF, rehab, or assisted living',
          'Wheelchair and ambulatory options with trained drivers and safe transfer support',
          'Recurring transportation for outpatient treatment schedules (dialysis, therapy, infusion)',
          'Transportation between healthcare facilities for continued care',
          'Coordination that respects facility timing, paperwork, and handoff protocols',
        ],
      }}
      capabilities={{
        title: 'Facility-Focused NEMT Capabilities',
        items: [
          { icon: Hospital, title: 'Hospital Discharge Transport', desc: 'Same-day and scheduled discharge to home, SNF, rehab, or assisted living. Door-to-door or curb-to-curb assistance is coordinated at booking.' },
          { icon: Building2, title: 'Facility-to-Facility Transfers', desc: 'Non-emergency transport between healthcare facilities for continued care or specialized services.' },
          { icon: Repeat, title: 'Recurring Outpatient Transport', desc: 'Standing schedules for dialysis, therapy, infusion, and other recurring outpatient appointments.' },
          { icon: HeartPulse, title: 'Wheelchair &amp; Ambulatory Options', desc: 'Wheelchair-accessible vans and comfortable sedan/van transport for ambulatory patients.' },
          { icon: ClipboardList, title: 'Trip Documentation', desc: 'Operational records to support facility workflows, billing coordination, and quality tracking.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Communication', desc: 'Professional handling of patient information across dispatch, driver, and facility touchpoints.' },
        ],
      }}
      coordination={{
        title: 'Coordination That Fits Facility Workflows',
        steps: [
          { title: 'Facility Request', desc: 'Case manager, discharge planner, or coordinator contacts dispatch with patient details and pickup window.' },
          { title: 'Trip Confirmation', desc: 'Vehicle type assigned and ETA confirmed. Any authorization or payer information is captured.' },
          { title: 'Facility Handoff', desc: 'Driver checks in at nurse station or coordinator, supports transfer, and confirms handoff protocol.' },
          { title: 'Trip Completion', desc: 'Patient delivered to destination. Door-to-door or curb-to-curb assistance is coordinated at booking. Trip completion is documented.' },
        ],
      }}
      why={{
        title: 'A Transportation Partner Your Care Teams Can Rely On',
        items: [
          { icon: Hospital, title: 'Discharge-Ready Dispatch', desc: 'Same-day and unplanned discharge transportation supported with responsive dispatch.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport.' },
          { icon: Repeat, title: 'Recurring Schedule Reliability', desc: 'Standing runs for outpatient, dialysis, therapy, and treatment schedules.' },
          { icon: FileCheck, title: 'Credentialing-Ready', desc: 'Documentation, licensing, and insurance information ready for facility procurement review.' },
          { icon: Phone, title: 'Single Point of Contact', desc: 'Provider relations contact for facility partnership questions and escalations.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Verified coverage across Harris, Fort Bend, Brazoria, Montgomery, and Galveston Counties.' },
        ],
      }}
      transportRequest={{
        title: 'Submit a Facility Transportation Request',
        description: 'Discharge planners, case managers, and facility transport coordinators can submit non-emergency transportation requests directly to MoveraCare dispatch.',
        cta: { label: 'Request Patient Transportation', to: '/request-patient-transportation' },
      }}
      vendorRelationship={{
        title: 'Start a Provider / Vendor Relationship',
        description: 'For facility administrators, procurement, and network teams evaluating MoveraCare — download our vendor procurement packet or contact Provider Relations to begin credentialing.',
      }}
    />
  );
};

export default HealthcareFacilities;
